import * as React from 'react'

import { cn } from '@/components/new/lib/utils'

function composeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
    return (node: T | null) => {
        for (const ref of refs) {
            if (!ref) {
                continue
            }

            if (typeof ref === 'function') {
                ref(node)
                continue
            }

            try {
                ;(ref as React.MutableRefObject<T | null>).current = node
            } catch {
                // Ignore assignment issues for non-mutable refs.
            }
        }
    }
}

type SlotRootProps = React.HTMLAttributes<HTMLElement> & {
    children?: React.ReactNode
}

const SlotRoot = React.forwardRef<HTMLElement, SlotRootProps>(({ children, className, ...props }, forwardedRef) => {
    if (!React.isValidElement(children)) {
        return null
    }

    const child = children as React.ReactElement<{ className?: string; ref?: React.Ref<HTMLElement> }>
    const childProps = child.props ?? {}
    const childRef = (child as unknown as { ref?: React.Ref<HTMLElement> }).ref

    return React.cloneElement(child, {
        ...props,
        ...childProps,
        className: cn(className, childProps.className),
        ref: composeRefs(forwardedRef, childRef),
    })
})

SlotRoot.displayName = 'SlotRoot'

export const Slot = {
    Root: SlotRoot,
}
